import { useState } from 'react'
import { Modal } from '../ui/Modal'
import { StatusBadge } from '../ui/StatusBadge'
import { LoadingSpinner } from '../ui/LoadingSpinner'
import { ImageGallery } from './ImageGallery'
import { EditSessionPanel } from './EditSessionPanel'
import { OutpaintingPanel } from './OutpaintingPanel'
import { AddToCollectionModal } from './AddToCollectionModal'
import { Clock, ExternalLink, AlertCircle, RotateCcw, MessageSquareText, Image as ImageIcon2, Maximize2, FolderPlus } from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import { es } from 'date-fns/locale'
import type { VisualTask, GeneratedImage } from '../../lib/api'
import { removeImageBackground } from '../../lib/api'
import { useAppStore } from '../../stores/app'
import { useQueryClient } from '@tanstack/react-query'

type DetailTab = 'images' | 'outpaint'

interface VisualTaskDetailProps {
  isOpen: boolean
  onClose: () => void
  task: VisualTask | null
  images: GeneratedImage[] | null
  isLoading: boolean
  onCancel?: () => void
  onRetry?: (taskId: string) => void
  isRetrying?: boolean
}

export function VisualTaskDetail({
  isOpen,
  onClose,
  task,
  images,
  isLoading,
  onCancel,
  onRetry,
  isRetrying = false,
}: VisualTaskDetailProps) {
  const organizationId = useAppStore((s) => s.organizationId)
  const queryClient = useQueryClient()
  const [imageToEdit, setImageToEdit] = useState<GeneratedImage | null>(null)
  const [activeTab, setActiveTab] = useState<DetailTab>('images')
  const [collectionModalOpen, setCollectionModalOpen] = useState(false)

  const handleEditSuccess = () => {
    if (task?.id) {
      queryClient.invalidateQueries({ queryKey: ['task_images', task.id] })
      queryClient.invalidateQueries({ queryKey: ['visual_task', task.id] })
    }
    setImageToEdit(null)
  }

  const handleRemoveBackground = async (image: GeneratedImage) => {
    await removeImageBackground(image.id)
    if (task?.id) {
      queryClient.invalidateQueries({ queryKey: ['task_images', task.id] })
      queryClient.invalidateQueries({ queryKey: ['visual_task', task.id] })
    }
  }

  const canCancel = task?.status === 'pending' || task?.status === 'running'
  const canRetry = (task?.status === 'failed' || task?.status === 'cancelled') && !!onRetry && !!task?.id

  const accompanyingText = images?.find((img) => img.accompanying_text)?.accompanying_text ?? null

  return (
    <>
    <Modal isOpen={isOpen} onClose={onClose} title="Detalles de Visual" size="xl">
      {isLoading ? (
        <div className="flex justify-center items-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      ) : task ? (
        <div className="space-y-6">
          {/* Task Metadata */}
          <div className="border-b border-slate-200 pb-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <StatusBadge status={task.status} />
                  <span className="px-2 py-1 bg-emerald-50 text-emerald-700 rounded text-xs font-medium">
                    {task.channel}
                  </span>
                  {task.template_id && (
                    <span className="px-2 py-1 bg-slate-100 text-slate-600 rounded text-xs">
                      Template: {task.template_id}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 ml-4">
                <button
                  type="button"
                  title="Agregar a coleccion"
                  onClick={() => setCollectionModalOpen(true)}
                  className="btn btn-secondary text-sm inline-flex items-center gap-2"
                >
                  <FolderPlus className="w-4 h-4" />
                  Coleccion
                </button>
                {canRetry && (
                  <button
                    type="button"
                    onClick={() => onRetry(task.id)}
                    disabled={isRetrying}
                    className="btn btn-primary text-sm inline-flex items-center gap-2"
                  >
                    {isRetrying ? (
                      <LoadingSpinner size="sm" />
                    ) : (
                      <RotateCcw className="w-4 h-4" />
                    )}
                    Reintentar
                  </button>
                )}
                {canCancel && onCancel && (
                  <button
                    onClick={onCancel}
                    className="btn btn-secondary text-sm"
                  >
                    Cancelar
                  </button>
                )}
              </div>
            </div>

            {/* Error Message */}
            {task.status === 'failed' && task.prompt_data?.error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-red-900">Error en la generación</p>
                    <p className="text-sm text-red-700 mt-1">{task.prompt_data.error}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Metadata */}
            <div className="flex items-center gap-6 text-sm text-slate-600">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span>
                  Creada{' '}
                  {formatDistanceToNow(new Date(task.created_at), {
                    addSuffix: true,
                    locale: es,
                  })}
                </span>
              </div>

              {task.completed_at && (
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  <span>
                    Completada{' '}
                    {formatDistanceToNow(new Date(task.completed_at), {
                      addSuffix: true,
                      locale: es,
                    })}
                  </span>
                </div>
              )}

              {task.content_task_id && (
                <a
                  href={`/content/${task.content_task_id}`}
                  className="flex items-center gap-1 text-emerald-600 hover:text-emerald-700 font-medium"
                >
                  <ExternalLink className="w-4 h-4" />
                  Ver contenido
                </a>
              )}
            </div>

            {/* Prompt Data */}
            {task.prompt_data?.prompt_text && (
              <div className="mt-4">
                <p className="text-xs text-slate-500 mb-2">Prompt:</p>
                <div className="bg-slate-50 p-3 rounded-lg text-sm text-slate-700">
                  {task.prompt_data.prompt_text}
                </div>
              </div>
            )}

            {/* Accompanying text from the model */}
            {accompanyingText && (
              <div className="mt-4 flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-lg p-4">
                <MessageSquareText className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs font-medium text-blue-700 mb-1">Comentario del modelo</p>
                  <p className="text-sm text-blue-900 whitespace-pre-wrap">{accompanyingText}</p>
                </div>
              </div>
            )}
          </div>

          {/* Tab bar */}
          <div className="flex gap-1 border-b border-slate-200 mb-4">
            <button
              type="button"
              onClick={() => setActiveTab('images')}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-t border-b-2 transition-colors ${
                activeTab === 'images'
                  ? 'border-emerald-500 text-emerald-700'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              <ImageIcon2 className="w-4 h-4" />
              Imágenes ({images?.length || 0})
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('outpaint')}
              disabled={!images || images.length === 0}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-t border-b-2 transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                activeTab === 'outpaint'
                  ? 'border-emerald-500 text-emerald-700'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              <Maximize2 className="w-4 h-4" />
              Expandir
            </button>
          </div>

          {/* Tab content */}
          {activeTab === 'images' && (
            <div>
              {images && images.length > 0 ? (
                <>
                  <ImageGallery
                    images={images}
                    onRequestEdit={organizationId ? (img) => setImageToEdit(img) : undefined}
                    onRemoveBackground={organizationId ? handleRemoveBackground : undefined}
                  />
                  {organizationId && (
                    <EditSessionPanel
                      isOpen={!!imageToEdit}
                      onClose={() => setImageToEdit(null)}
                      image={imageToEdit}
                      allImages={images}
                      contentId={task.content_id}
                      visualTaskId={task.id}
                      onSuccess={handleEditSuccess}
                    />
                  )}
                </>
              ) : task.status === 'completed' ? (
                <div className="text-center py-8 text-slate-500">
                  <p>No se generaron imágenes</p>
                </div>
              ) : task.status === 'pending' || task.status === 'running' ? (
                <div className="text-center py-8 text-slate-500">
                  <LoadingSpinner size="lg" />
                  <p className="mt-3">Generando imágenes...</p>
                </div>
              ) : null}
            </div>
          )}

          {activeTab === 'outpaint' && images && images.length > 0 && (
            <OutpaintingPanel
              images={images}
              onSuccess={() => {
                if (task?.id) {
                  queryClient.invalidateQueries({ queryKey: ['task_images', task.id] })
                  queryClient.invalidateQueries({ queryKey: ['visual_task', task.id] })
                }
              }}
            />
          )}
        </div>
      ) : (
        <div className="text-center py-8 text-slate-500">
          <p>No se pudo cargar la informacion de la tarea.</p>
        </div>
      )}
    </Modal>
    {task && (
      <AddToCollectionModal
        isOpen={collectionModalOpen}
        onClose={() => setCollectionModalOpen(false)}
        taskId={task.id}
      />
    )}
    </>
  )
}
